import { useBulkResolveContacts } from "@components/virtual-receptionist/contacts/hooks/useBulkResolveContacts";
import { getDisplayName, getRoleLabel } from "@components/virtual-receptionist/contacts/types/bulk-resolver.types";
import { Box, Button, List } from "@mui/material";
import { useMemo } from "react";
import { formatUSPhone } from "src/utils";
import { useContactDetailsModal } from "../../contacts/hooks/useContactDetailsModal";
import { useVirtualAssistant } from "../../context/VirtualAssistantContext";
import { useInfiniteCallHistoryQuery } from "../hooks/useInfiniteCallHistoryQuery";
import { UiCall } from "../types";
import { CallEmptyState } from "./CallEmptyState";
import { CallItem } from "./CallItem";
import { ContactCenterLoadingBody } from "../../ContactCenterLoadingBody";

export const CallHistory = () => {
  const { sessionNumber } = useVirtualAssistant();
  const { openContactDetails, openAddContactMenu } = useContactDetailsModal();

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } = useInfiniteCallHistoryQuery({
    sessionNumber,
    limit: 20,
  });

  const rawCalls = useMemo(() => {
    if (!data?.pages) return [];
    return data.pages.flatMap((page) => page.data);
  }, [data]);

  const unresolvedPhones = useMemo(() => {
    const phones = rawCalls
      .filter((call) => call.provider === "twilio" && !call.contact)
      .map((call) => call.peerId)
      .filter(Boolean);

    return Array.from(new Set(phones));
  }, [rawCalls]);

  const { data: bulkResolvedMap = {} } = useBulkResolveContacts(unresolvedPhones);

  const uiCalls: UiCall[] = useMemo(() => {
    return rawCalls.map((call) => {
      if (call.provider === "webrtc") {
        return {
          id: call.uid,
          provider: call.provider,
          phoneNumber: call.peerId,
          peerId: call.peerId,
          displayName: call.peerName ?? null,
          callerType: null,
          isKnown: !!call.peerName,
          isLoadingCallerInfo: false,
          type: call.direction === "inbound" ? "incoming" : "outgoing",
          status: call.status,
          durationSeconds: call.duration,
          timestamp: new Date(call.startedAt),
          recordingUrl: call.recordingUrl ?? null,
          recordingDuration: call.recordingDuration ?? null,
          contact: null,
        };
      }

      const resolved = call.contact || bulkResolvedMap?.[call.peerId] || null;
      const isResolved = !call.contact && !!bulkResolvedMap?.[call.peerId];
      const contactWithResolvedFlag = resolved ? (isResolved ? { ...resolved, isResolved: true } : resolved) : null;

      return {
        id: call.uid,
        provider: call.provider,
        phoneNumber: call.peerId,
        peerId: call.peerId,
        displayName: contactWithResolvedFlag
          ? getDisplayName(contactWithResolvedFlag) || formatUSPhone(call.peerId)
          : null,
        callerType: contactWithResolvedFlag ? getRoleLabel(contactWithResolvedFlag) : null,
        isKnown: !!contactWithResolvedFlag,
        isLoadingCallerInfo: false,
        type: call.direction === "inbound" ? "incoming" : "outgoing",
        status: call.status,
        durationSeconds: call.duration,
        timestamp: new Date(call.startedAt),
        recordingUrl: call.recordingUrl ?? null,
        recordingDuration: call.recordingDuration ?? null,
        contact: contactWithResolvedFlag,
      };
    });
  }, [rawCalls, bulkResolvedMap]);

  if (isLoading && uiCalls.length === 0) {
    return <ContactCenterLoadingBody message="Loading call history..." />;
  }

  if (uiCalls.length === 0) {
    return <CallEmptyState />;
  }

  return (
    <Box width="100%" height="100%">
      <List sx={{ width: "100%", px: 1 }}>
        {uiCalls.map((call, index) => (
          <CallItem
            key={call.id}
            call={call}
            isLast={index === uiCalls.length - 1}
            onQuickSave={openAddContactMenu}
            onEditContact={(contact) => openContactDetails(call.phoneNumber, contact)}
          />
        ))}
      </List>

      {hasNextPage && (
        <Box display="flex" justifyContent="center" py={1}>
          <Button size="small" onClick={() => fetchNextPage()} disabled={isFetchingNextPage}>
            {isFetchingNextPage ? "Loading..." : "Load more"}
          </Button>
        </Box>
      )}
    </Box>
  );
};
